// src/pages/prompts/PromptList.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Box, Typography, Button, Paper, Table, TableBody, TableCell, 
  TableContainer, TableHead, TableRow, IconButton, Dialog, // Chip removed
  DialogActions, DialogContent, DialogContentText, DialogTitle,
  CircularProgress, Tooltip, Card, CardContent, Alert,
  TablePagination, TextField, InputAdornment
} from '@mui/material';
import { 
  Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon,
  Search as SearchIcon, Refresh as RefreshIcon, // ActiveIcon removed
  Info as InfoIcon
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { promptService } from '../../api/promptService';

const PromptList = () => {
  const [prompts, setPrompts] = useState([]);
  const [filteredPrompts, setFilteredPrompts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [promptToDelete, setPromptToDelete] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const navigate = useNavigate();

  useEffect(() => {
    loadPrompts();
  }, []);

  useEffect(() => {
    if (searchQuery.trim() === '') {
      setFilteredPrompts(prompts);
    } else {
      const lowercaseQuery = searchQuery.toLowerCase();
      const filtered = prompts.filter(prompt => 
        prompt.name.toLowerCase().includes(lowercaseQuery) ||
        prompt.content.toLowerCase().includes(lowercaseQuery)
      );
      setFilteredPrompts(filtered);
    }
    setPage(0);
  }, [searchQuery, prompts]);

  const loadPrompts = async () => {
    try {
      setLoading(true);
      const response = await promptService.getAllPrompts();
      if (response && response.isError) {
        toast.error(response.message || 'Failed to load prompts');
        setPrompts([]);
        setFilteredPrompts([]);
      } else if (Array.isArray(response)) {
        setPrompts(response);
        setFilteredPrompts(response);
      } else {
        toast.error('Received unexpected data format for prompts.');
        setPrompts([]);
        setFilteredPrompts([]);
      }
    } catch (error) {
      toast.error('An unexpected error occurred while loading prompts.');
      console.error(error);
      setPrompts([]);
      setFilteredPrompts([]);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleRefresh = () => {
    setRefreshing(true);
    loadPrompts();
  };

  const handleEdit = (id) => {
    navigate(`/prompts/edit/${id}`);
  };

  const handleCreate = () => {
    navigate('/prompts/create');
  };

  const confirmDelete = (prompt) => {
    setPromptToDelete(prompt);
    setDeleteDialogOpen(true);
  };

  const handleDelete = async () => {
    if (!promptToDelete) return;
    try {
      // The promptService.deletePrompt will fail if the prompt is linked to an agent.
      // This behavior is documented in FE API docs.md (deletePrompt fails if linked)
      const response = await promptService.deletePrompt(promptToDelete.id);
      if (response && response.isError) {
        // Example: "Cannot delete prompt: it is currently linked to one or more agents."
        toast.error(response.message || 'Failed to delete prompt. It might be in use.');
      } else {
        toast.success('Prompt deleted successfully');
        loadPrompts();
      }
    } catch (error) {
      toast.error('An unexpected error occurred while deleting prompt.');
      console.error(error);
    } finally {
      setDeleteDialogOpen(false);
      setPromptToDelete(null);
    }
  };

  // handleActivate function removed

  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  if (loading && !refreshing) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }
  
  const emptyRows = page > 0 
    ? Math.max(0, (1 + page) * rowsPerPage - filteredPrompts.length) 
    : 0;
  
  const visiblePrompts = filteredPrompts.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3, alignItems: 'center' }}>
        <Typography variant="h4" component="h1">
          System Prompts
        </Typography>
        <Button 
          variant="contained" 
          startIcon={<AddIcon />}
          onClick={handleCreate}
        >
          Create New Prompt
        </Button>
      </Box>

      <Alert 
        severity="info" 
        icon={<InfoIcon />}
        sx={{ mb: 3 }}
      >
        System prompts define how an AI agent responds. Prompts are linked to agents and made active
        within an agent's "Prompt" configuration tab.
      </Alert>

      <Card sx={{ mb: 3 }}>
        <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <TextField
              placeholder="Search prompts..."
              variant="outlined"
              size="small"
              value={searchQuery}
              onChange={handleSearchChange}
              sx={{ width: '300px' }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
            <Tooltip title="Refresh prompts">
              <IconButton onClick={handleRefresh} disabled={refreshing}>
                <RefreshIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </CardContent>
      </Card>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              {/* Status Column Removed */}
              <TableCell>Created</TableCell>
              <TableCell>Last Updated</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {refreshing ? (
              <TableRow>
                <TableCell colSpan={4} align="center" sx={{ py: 5 }}> {/* Adjusted colSpan */}
                  <CircularProgress size={30} />
                  <Typography variant="body2" sx={{ mt: 1 }}>Refreshing prompts...</Typography>
                </TableCell>
              </TableRow>
            ) : visiblePrompts.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} align="center" sx={{ py: 5 }}> {/* Adjusted colSpan */}
                  {searchQuery ? (
                    <><Typography variant="body1">No prompts match your search</Typography><Typography variant="body2" color="textSecondary">Try a different search term or clear the search</Typography></>
                  ) : (
                    <><Typography variant="body1">No prompts found</Typography><Button variant="outlined" startIcon={<AddIcon />} onClick={handleCreate} sx={{ mt: 2 }}>Create your first prompt</Button></>
                  )}
                </TableCell>
              </TableRow>
            ) : (
              visiblePrompts.map((prompt) => (
                <TableRow key={prompt.id} hover>
                  <TableCell>
                    <Typography variant="body1">
                      {prompt.name}
                    </Typography>
                    <Typography variant="caption" color="textSecondary" sx={{ display: 'block', maxWidth: '400px' }}>
                      {prompt.content.length > 100 ? `${prompt.content.substring(0, 100)}...` : prompt.content}
                    </Typography>
                  </TableCell>
                  {/* Status Cell Removed */}
                  <TableCell>
                    {new Date(prompt.created_at).toLocaleDateString()} 
                    <Typography variant="caption" color="textSecondary" display="block">{new Date(prompt.created_at).toLocaleTimeString()}</Typography>
                  </TableCell>
                  <TableCell>
                    {new Date(prompt.updated_at).toLocaleDateString()}
                    <Typography variant="caption" color="textSecondary" display="block">{new Date(prompt.updated_at).toLocaleTimeString()}</Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                      {/* Activate Button Removed */}
                      <Tooltip title="Edit prompt"><IconButton color="primary" onClick={() => handleEdit(prompt.id)}><EditIcon /></IconButton></Tooltip>
                      <Tooltip title="Delete prompt"><IconButton color="error" onClick={() => confirmDelete(prompt)}><DeleteIcon /></IconButton></Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            )}
            {emptyRows > 0 && (<TableRow style={{ height: 73 * emptyRows }}><TableCell colSpan={4} /></TableRow>)} {/* Adjusted colSpan */}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={filteredPrompts.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </TableContainer>

      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete the prompt "{promptToDelete?.name}"? This action cannot be undone.
            {/* Modified warning: deletePrompt checks if it's linked, so this specific warning is less direct from here */}
            <Alert severity="warning" sx={{ mt: 2 }}>
              Warning: Deleting this prompt will fail if it is currently linked to any agents.
              You must unlink it from all agents first.
            </Alert>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDelete} color="error">Delete</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PromptList;